import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { useConfig } from '../../system/providers/ConfigProvider';

const cookies = new Cookies();

const injectGA = (trackingId) => {
  if (!trackingId) return false;
  if (document.getElementById('ga-gtag')) return true;
  const s = document.createElement('script');
  s.id = 'ga-gtag';
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', trackingId);
  return true;
};

const injectGTM = (containerId) => {
  if (!containerId) return false;
  if (document.getElementById('gtm-script')) return true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const s = document.createElement('script');
  s.id = 'gtm-script';
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(s);
  return true;
};

const Analytics = () => {
  const { config } = useConfig();
  const location = useLocation();
  const [consent, setConsent] = useState(cookies.get('cookieConsent'));
  const gaInit = useRef(false);
  const gtmInit = useRef(false);
  const clientIdRef = useRef(null);

  useEffect(() => {
    let cid = cookies.get('hkai_client_id');
    if (!cid) {
      cid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      cookies.set('hkai_client_id', cid, { path: '/', maxAge: 60 * 60 * 24 * 365 });
    }
    clientIdRef.current = cid;
  }, []);

  const recordEvent = useCallback((type, data) => {
    const ev = { type, path: location.pathname + location.search, data, ts: Date.now(), clientId: clientIdRef.current };
    try {
      const k = 'hkai_events';
      const arr = JSON.parse(localStorage.getItem(k) || '[]');
      arr.unshift(ev);
      if (arr.length > 1000) arr.length = 1000;
      localStorage.setItem(k, JSON.stringify(arr));
    } catch {}
    try {
      fetch('http://localhost:4000/api/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ev)
      }).catch(() => {});
    } catch {}
  }, [location]);

  useEffect(() => {
    const handler = (e) => {
      if (e && e.detail && e.detail.consent) {
        setConsent(e.detail.consent);
      } else {
        setConsent(cookies.get('cookieConsent'));
      }
    };
    window.addEventListener('cookieConsentChanged', handler);
    return () => window.removeEventListener('cookieConsentChanged', handler);
  }, []);

  useEffect(() => {
    if (consent !== 'true') return;
    const gaCfg = config?.analytics?.googleAnalytics;
    const gtmCfg = config?.analytics?.googleTagManager;
    if (gaCfg?.enabled && gaCfg?.trackingId && !gaInit.current) {
      gaInit.current = injectGA(gaCfg.trackingId);
    }
    if (gtmCfg?.enabled && gtmCfg?.containerId && !gtmInit.current) {
      gtmInit.current = injectGTM(gtmCfg.containerId);
    }
    window.hkaiTrackEvent = function (name, params) {
      if (window.gtag && gaCfg?.enabled) window.gtag('event', name, params || {});
      if (window.dataLayer && gtmCfg?.enabled) window.dataLayer.push({ event: name, ...(params || {}) });
    };
  }, [config, consent]);

  useEffect(() => {
    if (consent !== 'true') return;
    const path = location.pathname + location.search;
    const gaCfg = config?.analytics?.googleAnalytics;
    const gtmCfg = config?.analytics?.googleTagManager;
    if (window.gtag && gaCfg?.enabled) {
      window.gtag('event', 'page_view', { page_path: path });
    }
    if (window.dataLayer && gtmCfg?.enabled) {
      window.dataLayer.push({ event: 'page_view', page_path: path });
    }
    recordEvent('page_view', {});
    try {
      const k = 'hkai_pageviews';
      const map = JSON.parse(localStorage.getItem(k) || '{}');
      map[path] = (map[path] || 0) + 1;
      localStorage.setItem(k, JSON.stringify(map));
    } catch {}
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ;['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
      const v = params.get(key);
      if (v) utm[key] = v;
    });
    if (Object.keys(utm).length) {
      try {
        sessionStorage.setItem('hkai_utm', JSON.stringify(utm));
        const hk = 'hkai_utm_history';
        const arr = JSON.parse(localStorage.getItem(hk) || '[]');
        arr.unshift({ ...utm, path, ts: Date.now() });
        if (arr.length > 200) arr.length = 200;
        localStorage.setItem(hk, JSON.stringify(arr));
      } catch {}
      recordEvent('utm', utm);
    }
  }, [location, config, consent, recordEvent]);

  const scrollFlagsRef = useRef({ 25: false, 50: false, 75: false, 100: false });

  useEffect(() => {
    if (consent !== 'true') return;
    scrollFlagsRef.current = { 25: false, 50: false, 75: false, 100: false };
    const path = location.pathname + location.search;
    const gaCfg = config?.analytics?.googleAnalytics;
    const gtmCfg = config?.analytics?.googleTagManager;
    const onScroll = () => {
      const doc = document.documentElement;
      const st = doc.scrollTop || document.body.scrollTop || 0;
      const sh = (doc.scrollHeight || 0) - (doc.clientHeight || 0);
      if (sh <= 0) return;
      const percent = Math.max(0, Math.min(100, Math.round((st / sh) * 100)));
      [25, 50, 75, 100].forEach((p) => {
        if (!scrollFlagsRef.current[p] && percent >= p) {
          scrollFlagsRef.current[p] = true;
          if (window.gtag && gaCfg?.enabled) {
            window.gtag('event', 'scroll_depth', { percent: p, page_path: path });
          }
          if (window.dataLayer && gtmCfg?.enabled) {
            window.dataLayer.push({ event: 'scroll_depth', percent: p, page_path: path });
          }
          try {
            const k = 'hkai_scroll_depth';
            const map = JSON.parse(localStorage.getItem(k) || '{}');
            map[path] = Math.max(map[path] || 0, p);
            localStorage.setItem(k, JSON.stringify(map));
          } catch {}
          recordEvent('scroll_depth', { percent: p });
        }
      });
    };
    const onClick = (e) => {
      const el = e.target.closest('a');
      if (!el) return;
      const href = el.getAttribute('href');
      if (!href) return;
      try {
        const u = new URL(href, window.location.origin);
        const isExternal = u.host !== window.location.host;
        if (isExternal) {
          if (window.gtag && gaCfg?.enabled) {
            window.gtag('event', 'outbound_click', { link_url: u.href, page_path: path });
          }
          if (window.dataLayer && gtmCfg?.enabled) {
            window.dataLayer.push({ event: 'outbound_click', link_url: u.href, page_path: path });
          }
          const k = 'hkai_outbound_clicks';
          const arr = JSON.parse(localStorage.getItem(k) || '[]');
          arr.unshift({ url: u.href, path, ts: Date.now() });
          if (arr.length > 200) arr.length = 200;
          localStorage.setItem(k, JSON.stringify(arr));
          recordEvent('outbound_click', { link_url: u.href });
        }
      } catch {}
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('click', onClick, true);
    };
  }, [location, config, consent, recordEvent]);

  useEffect(() => {
    if (consent !== 'true') return;
    const path = location.pathname + location.search;
    const gaCfg = config?.analytics?.googleAnalytics;
    const gtmCfg = config?.analytics?.googleTagManager;
    const sections = Array.from(document.querySelectorAll('[data-track-section]'));
    const flags = {};
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const id = el.getAttribute('data-track-section') || el.id || 'section';
        const ratio = Math.round((entry.intersectionRatio || 0) * 100);
        const checkpoints = [25, 50, 75, 100];
        flags[id] = flags[id] || { 25: false, 50: false, 75: false, 100: false };
        checkpoints.forEach((p) => {
          if (!flags[id][p] && ratio >= p) {
            flags[id][p] = true;
            if (window.gtag && gaCfg?.enabled) {
              window.gtag('event', 'section_view', { section_id: id, percent: p, page_path: path });
            }
            if (window.dataLayer && gtmCfg?.enabled) {
              window.dataLayer.push({ event: 'section_view', section_id: id, percent: p, page_path: path });
            }
            recordEvent('section_view', { sectionId: id, percent: p });
            try {
              const k = 'hkai_section_depth';
              const map = JSON.parse(localStorage.getItem(k) || '{}');
              map[path] = map[path] || {};
              map[path][id] = Math.max(map[path][id] || 0, p);
              localStorage.setItem(k, JSON.stringify(map));
            } catch {}
          }
        });
      });
    }, { threshold: [0.25, 0.5, 0.75, 1] });
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location, config, consent, recordEvent]);

  useEffect(() => {
    if (consent !== 'true') return;
    const path = location.pathname + location.search;
    const gaCfg = config?.analytics?.googleAnalytics;
    const gtmCfg = config?.analytics?.googleTagManager;
    const videos = Array.from(document.querySelectorAll('video'));
    const progressFlags = new Map();
    const onProgress = (video) => {
      const dur = video.duration || 0;
      if (!dur) return;
      const pct = Math.round(((video.currentTime || 0) / dur) * 100);
      const flags = progressFlags.get(video) || { 25: false, 50: false, 75: false, 100: false };
      [25, 50, 75, 100].forEach((p) => {
        if (!flags[p] && pct >= p) {
          flags[p] = true;
          progressFlags.set(video, flags);
          if (window.gtag && gaCfg?.enabled) {
            window.gtag('event', 'video_progress', { percent: p, src: video.currentSrc, page_path: path });
          }
          if (window.dataLayer && gtmCfg?.enabled) {
            window.dataLayer.push({ event: 'video_progress', percent: p, src: video.currentSrc, page_path: path });
          }
          recordEvent('video_event', { action: 'progress', percent: p, src: video.currentSrc });
        }
      });
    };
    const addListeners = (v) => {
      v.addEventListener('play', () => {
        if (window.gtag && gaCfg?.enabled) window.gtag('event', 'video_play', { src: v.currentSrc, page_path: path });
        if (window.dataLayer && gtmCfg?.enabled) window.dataLayer.push({ event: 'video_play', src: v.currentSrc, page_path: path });
        recordEvent('video_event', { action: 'play', src: v.currentSrc });
      });
      v.addEventListener('pause', () => {
        const dur = v.duration || 0;
        const pct = dur ? Math.round(((v.currentTime || 0) / dur) * 100) : 0;
        if (window.gtag && gaCfg?.enabled) window.gtag('event', 'video_pause', { percent: pct, src: v.currentSrc, page_path: path });
        if (window.dataLayer && gtmCfg?.enabled) window.dataLayer.push({ event: 'video_pause', percent: pct, src: v.currentSrc, page_path: path });
        recordEvent('video_event', { action: 'pause', percent: pct, src: v.currentSrc });
      });
      v.addEventListener('ended', () => {
        if (window.gtag && gaCfg?.enabled) window.gtag('event', 'video_ended', { src: v.currentSrc, page_path: path });
        if (window.dataLayer && gtmCfg?.enabled) window.dataLayer.push({ event: 'video_ended', src: v.currentSrc, page_path: path });
        recordEvent('video_event', { action: 'ended', src: v.currentSrc });
      });
      v.addEventListener('timeupdate', () => onProgress(v));
    };
    videos.forEach(addListeners);
    return () => {
      videos.forEach((v) => {
        v.removeEventListener('timeupdate', () => onProgress(v));
      });
    };
  }, [location, config, consent, recordEvent]);

  return null;
};

export default Analytics;
